/* eslint-disable max-lines */
/* eslint-disable import/prefer-default-export */
import { casingFormat, formatName } from 'common/utils';
import { capitalizeName } from 'common/utils/titleCase';
import { map, omit } from 'lodash';
import { transformShipmentNotes } from 'api/shipment/transform';

const PRX_SORT_FIELD_MAP = {
  createdAt: 'created_timestamp',
  orderDate: 'created_timestamp',
  shipmentDate: 'created_timestamp',
  itemCount: 'item_count',
};

export const buildPrxSortBy = (sortBy) => ({
  field: PRX_SORT_FIELD_MAP[sortBy?.sortKey] || 'created_timestamp',
  direction: sortBy?.sortOrder || 'DESC',
});

export const transformListOrders = ({ listOrders }) => ({
  count: listOrders.count,
  orders: map(
    listOrders.orders,
    ({
      patient,
      partner,
      lineItems,
      orderId,
      orderStatus,
      requestNeedsByDate,
      createdAt,
      lastUpdated,
      totalInOrder,
      waitingOnFill,
      partnerAccountId,
      fillType,
      state,
      fulfillingPharmacyId,
      source,
      isReshipment,
    }) => {
      const { patientDemographics, mpi } = patient;
      const { firstName, lastName } = patientDemographics;

      return {
        orderId,
        mpi,
        patientName: `${casingFormat(formatName(lastName))}, ${casingFormat(
          formatName(firstName)
        )}`,
        partnerName: capitalizeName(partner.partnerName),
        // Same shape listPatientOrders gives, so the order screen knows its partner before that
        // enrichment lands.
        partner: {
          partnerName: capitalizeName(partner.partnerName),
          partnerUuid: partner.partnerUuid,
        },
        orderStatus,
        lineItems,
        requestNeedsByDate,
        createdAt,
        lastUpdated,
        waitingOnFill,
        totalInOrder,
        partnerAccountId,
        fillType,
        state,
        fulfillingPharmacyId,
        source,
        isReshipment,
      };
    }
  ),
});

export const transformOrderShipment = ({ createOrderShipment: orderShipment }) => {
  const { orderIds, orderShipmentId, shipmentData, spOrderId, shipmentStatus } = orderShipment;

  return {
    shipmentId: orderShipmentId,
    createdDate: shipmentData.createDate,
    orderIds,
    shipstationId: spOrderId,
    status: shipmentStatus,
    trackingNumber: shipmentData.trackingNumber,
    carrierCode: shipmentData.carrierCode,
  };
};

export const transformListOrdersForShipment = ({ listOrdersForShipment }) => ({
  count: listOrdersForShipment.count,
  orders: map(
    listOrdersForShipment.orders,
    ({ patient, orderId, orderStatus, requestNeedsByDate, lastUpdated }) => {
      const { patientDemographics, mpi } = patient;
      const patientName = `${patientDemographics.lastName}, ${patientDemographics.firstName}`;

      return {
        orderId,
        mpi,
        patientName,
        orderStatus,
        requestNeedsByDate,
        lastUpdated,
      };
    }
  ),
});

export const transformListPomOrders = ({ listPomOrders }) => ({
  count: listPomOrders.count,
  orders: map(listPomOrders.result, (order) => ({
    ...omit(order, 'patient'),
    patientName: `${casingFormat(formatName(order.patient.lastName))}, ${casingFormat(
      formatName(order.patient.firstName)
    )}`,
    partnerName: `${casingFormat(order?.partnerName)}`,
    mpi: order.patient.mpi,
    drugName: order.drugName ? casingFormat(order.drugName) : order.drugName,
    strength: order.strength ? casingFormat(order.strength) : order.strength,
  })),
});

export const transformProcessedExternalOrders = ({ listProcessedExternalOrders }) => ({
  count: listProcessedExternalOrders.count,
  orders: map(listProcessedExternalOrders.orders, (order) => ({
    ...order,
    patientName: `${casingFormat(formatName(order.patientLastName))}, ${casingFormat(
      formatName(order.patientFirstName)
    )}`,
  })),
});

export const transformListBatchShipments = ({ listBatchShipments }) => ({
  count: listBatchShipments.count,
  orders: listBatchShipments.result,
});

export const transformShipmentLineItems = ({ listShipmentLineItems }) => ({
  count: listShipmentLineItems.count,
  shipmentLineItems: listShipmentLineItems.result.map((lineItem) => {
    const { patient, drugName } = lineItem;

    return {
      ...lineItem,
      drugName: drugName ? casingFormat(drugName) : drugName,
      patient: {
        ...patient,
        firstName: `${casingFormat(formatName(patient.firstName))}`,
        lastName: `${casingFormat(formatName(patient.lastName))}`,
      },
      patientName: `${casingFormat(formatName(patient.lastName))}, ${casingFormat(
        formatName(patient.firstName)
      )}`,
      mpi: patient.mpi,
    };
  }),
});

export const transformReconcileOrderSuggestions = ({ getOrderSuggestions }) => {
  return {
    count: getOrderSuggestions.count,
    result: getOrderSuggestions.result.map((suggestion) => {
      const { firstName, lastName, ...restPatient } = suggestion.patient;

      return {
        ...suggestion,
        patient: {
          ...restPatient,
          firstName: casingFormat(formatName(firstName)),
          lastName: casingFormat(formatName(lastName)),
        },
      };
    }),
  };
};

export const transformUnReconcileShipments = ({ listUnReconcileOrders }) => ({
  count: listUnReconcileOrders.count,
  result: listUnReconcileOrders.result.map((item) => {
    return {
      ...item,
      patientName: casingFormat(formatName(item.patientName)),
    };
  }),
});

export const transformBatchShipments = ({ getBatchShipments }) => ({
  count: getBatchShipments.count,
  response: getBatchShipments.response.map((item) => {
    return {
      ...item,
      patientFirstName: casingFormat(formatName(item.patientFirstName)),
      patientLastName: casingFormat(formatName(item.patientLastName)),
      drugName: item.drugName ? casingFormat(item.drugName) : item.drugName,
    };
  }),
});

export const transformPrxUpdateShipmentResponse = (shipment) => {
  const { shippingAddress, originalShippingAddress, packageDimensions } = shipment;

  return {
    shipmentId: shipment.shipmentId,
    referenceId: shipment.referenceId,
    cost: shipment.cost,
    carrier: shipment.carrier,
    serviceCode: shipment.carrierServiceCode,
    packageCode: shipment.carrierPackageCode,
    enableSaturdayDelivery: shipment.saturdayShipping,
    signatureRequired: shipment.signatureRequired,
    orderWeight: shipment.orderWeight,
    dimensions: packageDimensions,
    shipTo: shippingAddress
      ? {
          nameOnAddress: shippingAddress.nameOnAddress,
          phoneNumber: shippingAddress.phoneNumber,
          addressDirectoryId: shippingAddress.addressDirectoryId,
          ...shippingAddress.address,
          isVerified: shipment.addressValidity === 'VALID' || shipment.addressValidity === 'WARN',
          addressValidity: shipment.addressValidity,
          addressDeficiencies: shipment.addressDeficiencies,
        }
      : undefined,
    originalShipmentAddress: originalShippingAddress
      ? {
          ...originalShippingAddress.address,
          addressDirectoryId: originalShippingAddress.addressDirectoryId,
        }
      : undefined,
    originalServiceCode: shipment.originalCarrierServiceCode,
    deliveryInstructions: shipment.deliveryInstructions,
    tags: shipment.tags || [],
    validatedShipTo: shipment.validatedShippingAddress
      ? {
          nameOnAddress: shipment.validatedShippingAddress.nameOnAddress,
          phoneNumber: shipment.validatedShippingAddress.phoneNumber,
          ...shipment.validatedShippingAddress.address,
          addressValidity: shipment.addressValidity,
          addressDeficiencies: shipment.addressDeficiencies,
          addressDirectoryId: shipment.validatedShippingAddress.addressDirectoryId,
        }
      : undefined,
  };
};

export const transformPrxShipmentListResponse = ({ listShipments }) => {
  const { shipments, totalCount } = listShipments;

  const result = map(shipments, (shipment) => {
    const { shippingAddress, items, orderWeight, clientMetadata, packageDimensions } = shipment;
    const { address, phoneNumber, addressDirectoryId } = shippingAddress;

    const isVerified = shipment.addressValidity === 'VALID' || shipment.addressValidity === 'WARN';

    const shipmentItems = map(items, (item) => ({
      orderId: item.externalOriginReferenceId,
      lineItemId: item.referenceId,
      sku: item.sku,
      name: item.name,
      quantity: item.quantity,
      weight: { value: item.weight.value, units: item.weight.units },
    }));

    return {
      isManualShipment: shipment.isManualShipment,
      referenceId: shipment.referenceId,
      shipmentId: shipment.shipmentId,
      isBatchShipment: shipment.isBatchedExternal,
      isBusinessAddress: shipment.addressType === 'BUSINESS',
      shipmentWeight: { value: orderWeight.value, units: orderWeight.units },
      recipientName: shipment.recipientName,
      fulfillingPharmacyId: shipment.fulfillingPharmacyId,
      partnerName: shipment.partnerName,
      practiceName: shipment.practiceName,
      serviceCode: shipment.carrierServiceCode,
      carrierCode: shipment.carrier,
      trackingNumber: shipment.trackingNumber,
      packageCode: shipment.carrierPackageCode,
      shipmentCreatedAt: shipment.createdTimestamp,
      shipmentShippedAt: shipment.shippedTimestamp,
      voidedTimestamp: shipment.voidedTimestamp,
      pickedUpTimestamp: shipment.pickedUpTimestamp,
      outForDeliveryTimestamp: shipment.outForDeliveryTimestamp,
      deliveredTimestamp: shipment.deliveredTimestamp,
      shipmentStatus: shipment.status,
      shipmentAddress: {
        ...address,
        isVerified,
        addressDirectoryId,
        addressValidity: shipment.addressValidity,
        addressDeficiencies: shipment.addressDeficiencies,
      },
      shipmentPhoneNumber: phoneNumber,
      itemCount: items ? items.length.toString() : '0',
      shipmentItems,
      cost: shipment.cost,
      insuranceAmountInUSD: clientMetadata?.insuranceAmountInUSD,
      partnerId: clientMetadata?.partnerId,
      practiceId: clientMetadata?.practiceId,
      shipDate: shipment.shipDate || undefined,
      dimensions: packageDimensions,
      enableSaturdayDelivery: shipment.saturdayShipping,
      signatureRequired: shipment.signatureRequired,
      deliveryInstructions: shipment.deliveryInstructions,
      originalShipmentAddress: shipment.originalShippingAddress
        ? {
            ...shipment.originalShippingAddress.address,
            addressDirectoryId: shipment.originalShippingAddress.addressDirectoryId,
          }
        : undefined,
      tags: shipment.tags || [],
      notes: transformShipmentNotes(shipment.notes),
      originalServiceCode: shipment.originalCarrierServiceCode,
      validatedShipmentAddress: shipment.validatedShippingAddress
        ? {
            ...shipment.validatedShippingAddress.address,
            addressDirectoryId: shipment.validatedShippingAddress.addressDirectoryId,
          }
        : undefined,
      enableInsurance: clientMetadata?.enableInsurance || false,
    };
  });

  return { count: totalCount, result };
};

export const transformPrxGenerateLabelResponse = ({ generateLabelForShipment }) => {
  const { shipment } = generateLabelForShipment;

  return {
    carrierServiceCode: shipment.carrierServiceCode,
    packageCode: shipment.carrierPackageCode,
    dimensions: shipment.packageDimensions,
    orderWeight: shipment.orderWeight,
    status: shipment.status,
    trackingNumber: shipment.trackingNumber,
    shippedTimestamp: shipment.shippedTimestamp,
    labelUrl: shipment.labelUrl,
    cost: shipment.cost,
    insuranceAmountInUSD: shipment.clientMetadata?.insuranceAmountInUSD,
  };
};
